import type { ApiEducationStatistics } from '@mfw/common';

import type {
  UpdateModuleMarketplaceConfigArgs,
  UnarchiveModuleByIdArgs,
  ArchiveModuleByIdArgs,
  FetchModuleByIdArgs,
  FetchModulesArgs,
  CreateModuleArgs,
  EditModuleArgs,
} from './types/modules.types';
import type { ApiModule } from '../schemas/modules.schema';
import type { BaseApiResponse } from './types/api.types';

import {
  createModuleApiArgs,
  mapModuleFromApi,
} from './mappers/modules.mappers';
import { postWithPagination, destroy, patch, post, get } from './axios';
import { DEFAULT_CURRENCY } from '../config/constants';

const base = (organizationId?: string) =>
  `/v2${organizationId ? `/organizations/${organizationId}` : ''}/education/modules`;

export const createModule = async ({
  organizationId,
  ...args
}: CreateModuleArgs) => {
  const response = await post<ApiModule>(
    base(organizationId),
    createModuleApiArgs(args),
  );
  if (!response?.data) return null;
  return mapModuleFromApi(response.data, args.locale);
};

export const editModuleById = async ({
  id,
  organizationId,
  ...args
}: EditModuleArgs) => {
  const response = await patch<ApiModule>(
    `${base(organizationId)}/${id}`,
    createModuleApiArgs(args),
  );
  if (!response?.data) return null;
  return mapModuleFromApi(response.data, args.locale);
};

export const fetchModuleById = async ({
  id,
  locale,
  organizationId,
}: FetchModuleByIdArgs) => {
  const response = await get<BaseApiResponse<ApiModule>>(
    `${base(organizationId)}/${id}`,
  );
  if (!response?.data) return null;
  return mapModuleFromApi(response.data, locale);
};

export const fetchModuleStatistics = async ({
  id,
  organizationId,
}: FetchModuleByIdArgs) => {
  const response = await get<BaseApiResponse<ApiEducationStatistics>>(
    `${base(organizationId)}/${id}/statistics`,
  );
  return response?.data ?? null;
};

export const fetchModules = async ({
  contentFilters,
  locale,
  organizationId,
  tableFilters,
}: FetchModulesArgs) => {
  const { search, status, ...restTableFilters } = tableFilters;
  const { authorIds, isHome, isMarketplace, sources, tags } =
    contentFilters || {};

  // active => isDeleted:false, archived => isDeleted:true, all => omit (both).
  const isDeleted =
    status === 'archived' ? true : status === 'all' ? undefined : false;

  const response = await postWithPagination<ApiModule[]>(
    `${base(organizationId)}/list`,
    {
      ...restTableFilters,
      ...(search ? { search } : {}),
      ...(tags ? { tagIds: tags } : {}),
      // no source keeps the organization's own view; the backend narrows to
      // exactly the sources picked (the platform library is one of them)
      ...(sources?.length ? { sources } : {}),
      ...(isDeleted !== undefined ? { isDeleted } : {}),
      authorIds,
      isHome,
      isMarketplace,
    },
  );
  if (!response?.data) return null;
  return {
    ...response,
    data: response.data.map((module) => mapModuleFromApi(module, locale)),
  };
};

export const archiveModuleById = async ({
  id,
  organizationId,
}: ArchiveModuleByIdArgs) => {
  await destroy(`${base(organizationId)}/${id}`);
  return true;
};

export const unarchiveModuleById = async ({
  id,
  organizationId,
}: UnarchiveModuleByIdArgs) => {
  await patch(`${base(organizationId)}/${id}/restore`);
  return true;
};

export const updateModuleMarketplaceConfig = async ({
  id,
  currency = DEFAULT_CURRENCY,
  enabled,
  organizationId,
  price,
}: UpdateModuleMarketplaceConfigArgs) => {
  await patch(`${base(organizationId)}/${id}/marketplace-config`, {
    configuration: {
      marketplace: { currency, enabled, price: enabled ? price : 0 },
    },
  });
};
